import React from 'react';
import { Check } from 'lucide-react';
import { isNative } from '../../utils/nativeBridge';
import { isVariantUnavailable } from './UnavailableBadge';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '../ui/drawer';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

/**
 * Variant selection UI shared by every themed product card — Bottom Sheet on
 * native/mobile, Dialog on web (same list content either way). Selecting a row
 * only calls onSelect(variant) and closes — it never adds to cart. Committing
 * to cart is still entirely the card's own existing Add/stepper button, which
 * this component never touches.
 */
const VariantPicker = ({
  open, onOpenChange, productId, productName, variants,
  selectedVariant, onSelect, currencySymbol, getItemQuantity, storeSettings,
}) => {
  if (!variants || variants.length === 0) return null;

  const title = productName ? `Select option — ${productName}` : 'Select option';

  const list = (
    <div className="max-h-[60vh] overflow-y-auto px-4 pb-4 space-y-2">
      {variants.map((v) => {
        const vOos = isVariantUnavailable(v, storeSettings);
        const isSelected = selectedVariant?.label === v.label;
        const vQty = getItemQuantity(productId, v.label);
        const discount = v.mrp > v.price ? Math.round((1 - v.price / v.mrp) * 100) : 0;
        return (
          <button
            key={v.label}
            disabled={vOos}
            onClick={() => { onSelect(v); onOpenChange(false); }}
            className={`w-full flex items-center justify-between p-3 rounded-xl border transition-all text-left ${
              vOos ? 'border-gray-200 opacity-50 cursor-not-allowed'
              : isSelected ? 'border-[var(--theme-primary)] bg-[var(--theme-primary)]/5'
              : 'border-black/10 hover:border-[var(--theme-primary)]/40'
            }`}
            data-testid={`variant-picker-${productId}-${v.label}`}
          >
            <div>
              <p className="text-sm font-medium text-[var(--theme-text-main)]">{v.label}</p>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-sm font-bold text-[var(--theme-text-main)]">{currencySymbol}{v.price}</span>
                {discount > 0 && <span className="text-xs text-[var(--theme-text-muted)] line-through">{currencySymbol}{v.mrp}</span>}
                {vOos && <span className="text-xs text-red-500 font-medium">Out of stock</span>}
                {!vOos && vQty > 0 && <span className="text-xs text-[var(--theme-primary)] font-medium">{vQty} in cart</span>}
              </div>
            </div>
            {isSelected && <Check className="w-5 h-5 text-[var(--theme-primary)] shrink-0" />}
          </button>
        );
      })}
    </div>
  );

  if (isNative()) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent data-testid="variant-picker-sheet">
          <DrawerHeader>
            <DrawerTitle>{title}</DrawerTitle>
          </DrawerHeader>
          {list}
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="variant-picker-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        {list}
      </DialogContent>
    </Dialog>
  );
};

export { VariantPicker };
